import React, { Suspense, lazy } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './styles.module.scss';
import {useUserContext} from 'providers/user-context';
import { Navigate } from 'react-router-dom';
import { APP_PATHS } from '../../constants';
import withAxiosInterceptor from 'hoc/axios-interceptor';
import SectionLoader from '../../components/section-loader';
import {CircleUserRound, UserRoundPen} from 'lucide-react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useVersion } from 'hooks/useVersion';

// Import critical section and data
import WelcomeSection from '../home/welcome-section';
import homeDate from '../home/home-data.json';

// Lazy load non-critical sections with simulated delay
const TestimonialsSection = lazy(() => import('../home/testimonials-section'));
const HowItWorksSection = lazy(() => import('../home/how-it-works'));
const KPIsSection = lazy(() => import('../home/kpi-section'));
const CashoutViaSection = lazy(() => 
  import('../home/cashout-via-section').then(module => ({ 
    default: module.CashoutViaSection 
  }))
);
const FAQAccordion = lazy(() => import('../home/faq-section'));

export const NativeHomePage: React.FC = () => {
  const {user} = useUserContext();
  const version = useVersion();

  // If the user is logged in, redirect to offers page (replace={true} ensures no back navigation)
  if (user) {
    return <Navigate to={APP_PATHS.OFFERS} replace={true} />;
  }

  // If version is not set show null to avoid rendering the page and flickering
  if (version === null) {
    return null;
  }

  return (
    <div className={styles.nativeHomeLayout}>

        {/* Slider with cards */}
        <Swiper
        modules={[Pagination]}
        spaceBetween={20}
        slidesPerView={1}
        pagination={{ clickable: true }}
        grabCursor={true}
        className={styles.swiper}
        >
            {/* Welcome Section */}
            <SwiperSlide className={styles.slide}>
                <div className={styles.slideContent}>
                    <WelcomeSection gameCards={homeDate.gameCards} version={version}/>
                </div>
            </SwiperSlide>

            {/* Cashout Via Section */}
            <SwiperSlide className={styles.slide}>
                <div className={styles.slideContent}>
                <Suspense fallback={<SectionLoader />}>
                    <CashoutViaSection />
                </Suspense>
                </div>
            </SwiperSlide>

            {/* Testimonials Section */}
            <SwiperSlide className={styles.slide}>
                <div className={styles.slideContent}>
                <Suspense fallback={<SectionLoader />}>
                    <TestimonialsSection testimonialsData={homeDate.testimonialsData} />
                </Suspense>
                </div>
            </SwiperSlide>

            {/* KPIs Section */}
            <SwiperSlide className={styles.slide}>
                <div className={styles.slideContent}>
                <Suspense fallback={<SectionLoader />}>
                    <KPIsSection kpisData={homeDate.kpisData} />
                </Suspense>
                </div>
            </SwiperSlide>

            {/* How It Works Section */}
            <SwiperSlide className={styles.slide}>
                <div className={styles.slideContent}>
                <Suspense fallback={<SectionLoader />}>
                    <HowItWorksSection howItWorksStep={homeDate.hiwData} />
                </Suspense>
                </div>
            </SwiperSlide>

            {/* FAQ Section */}
            <SwiperSlide className={styles.slide}>
                <div className={styles.slideContent}>
                <Suspense fallback={<SectionLoader />}>
                    <FAQAccordion faqItem={homeDate.faqData} />
                </Suspense>
                </div>
            </SwiperSlide>

            { /* Contact Us Section */}
            <SwiperSlide className={classNames([styles.slide, styles.contactUsSlide])}>
                <div className={styles.slideContent}>
                    <h2 className={styles.middleHeading}>Have a <span style={{color: '#FFB142'}}>Question?</span></h2>
                    <p className={styles.contactDescription}>
                        Our team is ready to help with any questions about earnings, payments technical issues, or anything else you need assistance with.
                    </p>
                    <Link to={APP_PATHS.CONTACT_US} className={styles.contactButton}>
                        Contact Support
                    </Link>
                </div>
            </SwiperSlide>
        </Swiper>

        {/* Signup/ Sign In buttons */}
        <div className={styles.authButtons}>
            <Link to={APP_PATHS.SIGN_UP}>
                <div className={classNames(styles.signUp, styles.btn)}>
                    <div>
                        <UserRoundPen stroke="currentColor" strokeWidth={1.5} />
                    </div>
                    Start Earning
                </div>
            </Link>

            <Link to={APP_PATHS.LOGIN}>
                <div className={classNames(styles.signIn, styles.btn)}>
                    <div>
                        <CircleUserRound stroke="currentColor" strokeWidth={1.5} />
                    </div>
                    Sign In
                </div>
            </Link>
        </div>

    </div>
  );
};

// Wrapping NativeHomePage with withAxiosInterceptor HOC to handle toats and redirects (if specified in response)
export default withAxiosInterceptor(NativeHomePage);
